
import React, { useState } from 'react';
import {Box,Card,CardContent,Typography,Button,TextField,Stack,MenuItem,Alert,CircularProgress,IconButton,Grid,RadioGroup,
  FormControlLabel,Radio,FormControl,FormLabel,Divider,Paper,Chip,Stepper,Step,StepLabel
} from '@mui/material';
import {ArrowBack,Warning,Send,Person,LocalHospital,Schedule,Info
} from '@mui/icons-material';
import { orderAssignmentAPI } from '../deliveryAgentAPI';

const FailureReasonScreen = ({ order, onBack, onFailureSubmitted }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  
  const [failureData, setFailureData] = useState({
    failureType: '',
    failureReason: '',
    failureDetails: '',
    canReschedule: 'yes',
    suggestedRescheduleTime: '',
    patientAvailable: 'no',
    addressIssue: 'no',
    additionalNotes: ''
  });

  const steps = ['Failure Type', 'Details', 'Review & Submit'];

  const failureTypes = [
    { 
      value: 'patient_unavailable', 
      label: 'Patient Not Available',
      icon: '👤',
      description: 'Patient was not present at the location'
    },
    { 
      value: 'patient_refused', 
      label: 'Patient Refused Collection',
      icon: '🚫',
      description: 'Patient declined to proceed with collection'
    },
    { 
      value: 'patient_unfit', 
      label: 'Patient Medically Unfit',
      icon: '🏥',
      description: 'Patient\'s medical condition prevented collection'
    },
    { 
      value: 'address_issue', 
      label: 'Address Issue',
      icon: '📍',
      description: 'Unable to locate or access the address'
    },
    { 
      value: 'access_denied', 
      label: 'Access Denied',
      icon: '🔒',
      description: 'Not allowed to enter location/premises'
    },
    { 
      value: 'equipment_failure', 
      label: 'Equipment/Material Issue',
      icon: '🔧',
      description: 'Technical or material problems'
    },
    { 
      value: 'sample_issue', 
      label: 'Sample Collection Problem',
      icon: '🧪',
      description: 'Unable to collect proper sample'
    },
    { 
      value: 'time_constraint', 
      label: 'Time Constraint',
      icon: '⏰',
      description: 'Time-related issues prevented collection'
    },
    { 
      value: 'other', 
      label: 'Other Reason',
      icon: '📝',
      description: 'Any other reason not listed above'
    }
  ];

  const commonReasons = {
    patient_unavailable: [
      'Patient not at home',
      'Patient sleeping/resting',
      'Patient in hospital',
      'Patient traveling',
      'No response at door',
      'Other'
    ],
    patient_refused: [
      'Changed mind about test',
      'Fear of needles/procedures',
      'Family objection',
      'Financial concerns',
      'Wants different time',
      'Other'
    ],
    patient_unfit: [
      'Patient too weak',
      'Patient unconscious',
      'Medical emergency',
      'High fever/illness',
      'Doctor advised postponement',
      'Other'
    ],
    address_issue: [
      'Address incomplete',
      'Address incorrect',
      'Location not accessible',
      'No such address exists',
      'Landmark not found',
      'Other'
    ],
    equipment_failure: [
      'Vials/containers insufficient',
      'Equipment malfunction',
      'Missing required materials',
      'Storage equipment issue',
      'Other'
    ],
    sample_issue: [
      'Unable to locate vein',
      'Patient difficult vein access',
      'Hemolysis occurred',
      'Insufficient sample volume',
      'Contamination risk',
      'Other'
    ],
    time_constraint: [
      'Traffic delay',
      'Multiple urgent bookings',
      'Patient requested later time',
      'Scheduling conflict',
      'Other'
    ],
    access_denied: [
      'Security denied entry',
      'Gate closed/locked',
      'Society/building restrictions',
      'Family member refused entry',
      'Other'
    ]
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFailureData({
      ...failureData,
      [field]: value,
      ...(field === 'failureType' && { failureReason: '' })
    });
  };

  const validateForm = () => {
    if (!failureData.failureType) {
      setError('Please select a failure type');
      return false;
    }
    if (!failureData.failureReason) {
      setError('Please select a specific reason');
      return false;
    }
    if (!failureData.failureDetails.trim()) {
      setError('Please provide detailed description');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (activeStep === 0 && !failureData.failureType) {
      setError('Please select a failure type to continue');
      return;
    }
    if (activeStep === 1 && !failureData.failureReason) {
      setError('Please select a specific reason to continue');
      return;
    }
    setError(null);
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmitFailure = async () => {
    try {
      setError(null);
      
      if (!validateForm()) {
        return;
      }
      
      setLoading(true);
      
      console.log('Submitting failure report:', {
        orderId: order.id,
        orderType: order.orderType,
        failureData
      });

      const response = await orderAssignmentAPI.reportCollectionFailure(
        order.id,
        order.orderType,
        {
          ...failureData,
          reportedAt: new Date().toISOString(),
          reportedBy: sessionStorage.getItem('userId')
        }
      );

      if (response.data.success) {
        console.log('Failure reported:', response.data);
        
        setTimeout(() => {
          onFailureSubmitted(failureData);
        }, 1000);
      } else {
        throw new Error(response.data.message || 'Failed to submit failure report');
      }
      
    } catch (err) {
      console.error('Failure report error:', err);
      setError(err.response?.data?.message || err.message || 'Error submitting failure report');
    } finally {
      setLoading(false);
    }
  };

  const availableReasons = commonReasons[failureData.failureType] || [];
  const selectedType = failureTypes.find(t => t.value === failureData.failureType);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 3, 
          background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
          color: 'white',
          borderRadius: 2
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          <IconButton 
            onClick={onBack} 
            disabled={loading}
            sx={{ 
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Box flex={1}>
            <Typography variant="h4" fontWeight={700}>
              Collection Failure Report
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
              Order #{order.orderId}
            </Typography>
          </Box>
          <Warning sx={{ fontSize: 48, opacity: 0.3 }} />
        </Stack>

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mt: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': { color: 'rgba(255,255,255,0.7)' },
                  '& .MuiStepLabel-label.Mui-active': { color: 'white', fontWeight: 600 },
                  '& .MuiStepLabel-label.Mui-completed': { color: 'white' },
                  '& .MuiStepIcon-root': { color: 'rgba(255,255,255,0.3)' },
                  '& .MuiStepIcon-root.Mui-active': { color: 'white' },
                  '& .MuiStepIcon-root.Mui-completed': { color: 'white' }
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }} 
          onClose={() => setError(null)}
          icon={<Warning />}
        >
          {error}
        </Alert>
      )}

      {/* Order Information Card */}
      <Card sx={{ mb: 3, borderLeft: '4px solid #006D77' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Info color="primary" />
            Order Information
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Person sx={{ fontSize: 16 }} />
                  Patient Name
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {order.customerName}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocalHospital sx={{ fontSize: 16 }} />
                  Service/Test
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {order.serviceName}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Schedule sx={{ fontSize: 16 }} />
                  Order ID
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  #{order.orderId}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Chip 
                  label="Collection Failed" 
                  color="error" 
                  size="small"
                  sx={{ width: 'fit-content', fontWeight: 600 }}
                />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Step Content */}
      <Box>
        {/* Step 1: Failure Type Selection */}
        {activeStep === 0 && (
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Select Failure Type
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Choose the primary reason why the sample collection could not be completed
              </Typography>

              <Grid container spacing={2}>
                {failureTypes.map((type) => (
                  <Grid item xs={12} sm={6} md={4} key={type.value}>
                    <Paper
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: failureData.failureType === type.value ? '#006D77' : '#e0e0e0',
                        bgcolor: failureData.failureType === type.value ? '#006D7710' : 'white',
                        transition: 'all 0.3s',
                        '&:hover': {
                          borderColor: '#006D77',
                          boxShadow: 2,
                          transform: 'translateY(-2px)'
                        }
                      }}
                      onClick={() => handleChange('failureType')({ target: { value: type.value } })}
                    >
                      <Stack spacing={1}>
                        <Box sx={{ fontSize: 32 }}>{type.icon}</Box>
                        <Typography variant="body1" fontWeight={600}>
                          {type.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {type.description}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Detailed Information */}
        {activeStep === 1 && (
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                {selectedType && <Box sx={{ fontSize: 32 }}>{selectedType.icon}</Box>}
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {selectedType?.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Provide detailed information about the failure
                  </Typography>
                </Box>
              </Stack>

              <Grid container spacing={3}>
                {/* Specific Reason */}
                {availableReasons.length > 0 && (
                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      label="Specific Reason *"
                      value={failureData.failureReason}
                      onChange={handleChange('failureReason')}
                      disabled={loading}
                      helperText="Select the most appropriate reason"
                    >
                      {availableReasons.map((reason) => (
                        <MenuItem key={reason} value={reason}>
                          {reason}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}

                {/* Detailed Description */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    label="Detailed Description *"
                    value={failureData.failureDetails}
                    onChange={handleChange('failureDetails')}
                    disabled={loading}
                    placeholder="Provide a comprehensive description of what happened, including any relevant context, attempts made, and observations..."
                    helperText={`${failureData.failureDetails.length}/500 characters - Be as specific as possible`}
                    inputProps={{ maxLength: 500 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {/* Can Reschedule */}
                <Grid item xs={12} md={6}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                      Can this collection be rescheduled? *
                    </FormLabel>
                    <RadioGroup
                      value={failureData.canReschedule}
                      onChange={handleChange('canReschedule')}
                    >
                      <FormControlLabel 
                        value="yes" 
                        control={<Radio />} 
                        label="Yes - Can be rescheduled"
                      />
                      <FormControlLabel 
                        value="no" 
                        control={<Radio />} 
                        label="No - Cannot be rescheduled"
                      />
                      <FormControlLabel 
                        value="uncertain" 
                        control={<Radio />} 
                        label="Uncertain - Needs review"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {/* Suggested Reschedule Time */}
                {failureData.canReschedule === 'yes' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="Suggested Reschedule Time"
                      value={failureData.suggestedRescheduleTime}
                      onChange={handleChange('suggestedRescheduleTime')}
                      disabled={loading}
                      InputLabelProps={{ shrink: true }}
                      helperText="When would be the best time to attempt collection again?"
                    />
                  </Grid>
                )}

                {/* Patient Available */}
                <Grid item xs={12} md={6}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                      Was the patient available at location?
                    </FormLabel>
                    <RadioGroup
                      row
                      value={failureData.patientAvailable}
                      onChange={handleChange('patientAvailable')}
                    >
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {/* Address Issue */}
                <Grid item xs={12} md={6}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                      Was there an address-related issue?
                    </FormLabel>
                    <RadioGroup
                      row
                      value={failureData.addressIssue}
                      onChange={handleChange('addressIssue')}
                    >
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {/* Additional Notes */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Additional Notes (Optional)"
                    value={failureData.additionalNotes}
                    onChange={handleChange('additionalNotes')}
                    disabled={loading}
                    placeholder="Any other relevant information, suggestions, or observations..."
                    helperText="Optional - Add any extra details that might be helpful"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Review & Submit */}
        {activeStep === 2 && (
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Review Your Report
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please review the information before submitting
              </Typography>

              <Stack spacing={2}>
                <Paper sx={{ p: 2, bgcolor: '#f8fafc' }}>
                  <Typography variant="caption" color="text.secondary">
                    Failure Type
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {selectedType?.icon} {selectedType?.label}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2, bgcolor: '#f8fafc' }}>
                  <Typography variant="caption" color="text.secondary">
                    Specific Reason
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {failureData.failureReason}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2, bgcolor: '#f8fafc' }}>
                  <Typography variant="caption" color="text.secondary">
                    Detailed Description
                  </Typography>
                  <Typography variant="body1">
                    {failureData.failureDetails}
                  </Typography>
                </Paper>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 2, bgcolor: '#f8fafc' }}>
                      <Typography variant="caption" color="text.secondary">
                        Can Reschedule
                      </Typography>
                      <Chip 
                        label={failureData.canReschedule.toUpperCase()} 
                        size="small"
                        color={failureData.canReschedule === 'yes' ? 'success' : failureData.canReschedule === 'no' ? 'error' : 'warning'}
                        sx={{ mt: 0.5 }}
                      />
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 2, bgcolor: '#f8fafc' }}>
                      <Typography variant="caption" color="text.secondary">
                        Patient Available
                      </Typography>
                      <Chip 
                        label={failureData.patientAvailable.toUpperCase()} 
                        size="small"
                        color={failureData.patientAvailable === 'yes' ? 'success' : 'error'}
                        sx={{ mt: 0.5 }}
                      />
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 2, bgcolor: '#f8fafc' }}>
                      <Typography variant="caption" color="text.secondary">
                        Address Issue
                      </Typography>
                      <Chip 
                        label={failureData.addressIssue.toUpperCase()} 
                        size="small"
                        color={failureData.addressIssue === 'yes' ? 'error' : 'success'}
                        sx={{ mt: 0.5 }}
                      />
                    </Paper>
                  </Grid>
                </Grid>

                {failureData.suggestedRescheduleTime && (
                  <Paper sx={{ p: 2, bgcolor: '#f8fafc' }}>
                    <Typography variant="caption" color="text.secondary">
                      Suggested Reschedule Time
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {new Date(failureData.suggestedRescheduleTime).toLocaleString()}
                    </Typography>
                  </Paper>
                )}

                {failureData.additionalNotes && (
                  <Paper sx={{ p: 2, bgcolor: '#f8fafc' }}>
                    <Typography variant="caption" color="text.secondary">
                      Additional Notes
                    </Typography>
                    <Typography variant="body1">
                      {failureData.additionalNotes}
                    </Typography>
                  </Paper>
                )}
              </Stack>

              <Alert severity="warning" sx={{ mt: 3 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Before Submitting
                </Typography>
                <Typography variant="body2">
                  This report will be reviewed by the operations team. Ensure all information is accurate and complete. 
                  Once submitted, this action cannot be undone.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Navigation Buttons */}
      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={activeStep === 0 ? onBack : handleBack}
            disabled={loading}
            fullWidth
            size="large"
            sx={{ py: 1.5 }}
          >
            {activeStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          
          {activeStep < 2 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading}
              fullWidth
              size="large"
              sx={{ 
                py: 1.5,
                bgcolor: '#006D77',
                '&:hover': { bgcolor: '#005561' }
              }}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              onClick={handleSubmitFailure}
              disabled={loading}
              fullWidth
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
              sx={{ py: 1.5 }}
            >
              {loading ? 'Submitting Report...' : 'SUBMIT FAILURE REPORT'}
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Help Text */}
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mt: 2 }}>
        <Info sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary">
          Need help? Contact support for assistance
        </Typography>
      </Stack>
    </Box>
  );
};

export default FailureReasonScreen;